package org.multiplatform.kotfolio.pages.coroutines

import androidx.compose.runtime.Composable
import org.multiplatform.kotfolio.components.KotlinCode
import org.multiplatform.kotfolio.layout.BlogLayout
import org.multiplatform.kotfolio.layout.BlogProperties
import org.multiplatform.kotfolio.layout.PageProperties
import com.varabyte.kobweb.compose.css.FontSize
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.silk.components.text.SpanText
import org.jetbrains.compose.web.css.em
import org.jetbrains.compose.web.dom.Li
import org.jetbrains.compose.web.dom.Ol
import org.jetbrains.compose.web.dom.Ul


@Page("/coroutines/convert_flow")
@Composable
fun ConvertFlowPage() {
    BlogLayout(
        PageProperties(
            "Convert Flow",
            "When use stateIn and shareIn. stateIn and shareIn are functions that are used to convert a cold Flow into a hot Shared Flow."
        ),
        BlogProperties("Coroutines", "stateIn and shareIn | Convert Flow")
    ) {
        Column(
            horizontalAlignment = Alignment.Start
        ) {

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "stateIn and shareIn are functions that are used to convert a cold Flow into a hot Shared Flow.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "They can multicast the information that comes from a cold upstream flow to multiple collectors.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "They’re often used to improve performance, add a buffer when collectors are not present, or even as a caching mechanism.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Ol {
                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Flow.ShareIn() converts Flow to SharedFlow",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Flow.StateIn() converts Flow to StateFlow",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }
            }

            SpanText(
                text = "stateIn",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.5.em).toAttrs()) {
                    SpanText(
                        text = "stateIn is an operator in Kotlin Flow that converts a cold flow into a hot StateFlow.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.5.em).toAttrs()) {
                    SpanText(
                        text = "Returns a StateFlow, and it synchronously caches and replays the most recent value emitted value by the Flow. The collected value can be read by using its value property.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.5.em).toAttrs()) {
                    SpanText(
                        text = "StateFlow is a hot flow—it remains in memory as long as the flow is collected or while any other references to it exist from a garbage collection root. You can turn cold flows hot by using the shareIn.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.5.em).toAttrs()) {
                    SpanText(
                        text = "A CoroutineScope that is used to share the flow. This scope should live longer than any consumer to keep the shared flow alive as long as needed.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.5.em).toAttrs()) {
                    SpanText(
                        text = "The number of items to replay to each new collector.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.5.em).toAttrs()) {
                    SpanText(
                        text = "The start behavior policy.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            //https://github.com/jordankurtz/PiAwareMobile
            KotlinCode(
                code = """
                    fun <T> Flow<Async<T>>.stateIn(scope: CoroutineScope): StateFlow<Async<T>> = stateIn(
                        scope,
                        SharingStarted.WhileSubscribed(stopTimeoutMillis = 5000),
                        Async.NotStarted
                    )
                    
                    sealed class Async<out T> {
                        object NotStarted : Async<Nothing>()
                        object Loading : Async<Nothing>()
                        class Success<T>(val data: T) : Async<T>()
                        data class Error(val message: String, val throwable: Throwable? = null) : Async<Nothing>()

                        fun getValue(): T? {
                            return when (this) {
                                is Error, Loading, NotStarted -> null
                                is Success<*> -> data as? T
                            }
                        }
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    val flow = flowOf(1, 2, 3, 4, 5)
                    .stateIn(
                        scope = this,
                        started = SharingStarted.WhileSubscribed(),
                        initialValue = 1
                    )
                    """.trimIndent()
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.5.em).toAttrs()) {
                    SpanText(
                        text = "scope: Specifies the coroutine scope in which sharing is started usually viewmodelScope in ViewModel.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.5.em).toAttrs()) {
                    SpanText(
                        text = "initialValue — The default value the StateFlow holds before any emissions.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.5.em).toAttrs()) {
                    SpanText(
                        text = "started: The strategy that controls when sharing is started and stopped.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.5.em).toAttrs()) {
                    SpanText(
                        text = "SharingStarted.WhileSubscribed(): Start policy keeps the upstream producer active while there are active subscribers. Other start policies are available, such as SharingStarted.Eagerly to start the producer immediately or SharingStarted.Lazily to start sharing after the first subscriber appears and keep the flow active forever. ",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            KotlinCode(
                code = """
                    class DashBoardViewModel() : ViewModel() {

                        private var hasFetched = false

                        private val _dashboardState = MutableStateFlow(DashboardState())
                        val dashboardState = _dashboardState.asStateFlow()
                            .onStart {
                                if(!hasFetched) {
                                    fetchTransactions()
                                    hasFetched = true
                                }
                            }
                            .stateIn(
                                scope = viewModelScope,
                                started = SharingStarted.WhileSubscribed(5_000L),
                                initialValue = DashboardState()
                            )

                        private fun fetchTransactions() {
                            viewModelScope.launch {
                              
                            }
                        }
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    fun getObjects(): Flow<List<MuseumObject>>

                    class DetailViewModel(
                        private val museumRepository: MuseumRepository,
                    ) : KMMViewModel() {

                        private val objectId = MutableStateFlow<Int?>(null)

                        @OptIn(ExperimentalCoroutinesApi::class)
                        @NativeCoroutinesState
                        val museumObject: StateFlow<MuseumObject?> = objectId
                            .flatMapLatest {
                                val id = it ?: return@flatMapLatest flowOf(null)
                                museumRepository.getObjectById(id)
                            }
                            .stateIn(
                                viewModelScope,
                                SharingStarted.WhileSubscribed(5000),
                                null,
                            )

                        fun setId(objectId: Int) {
                            this.objectId.value = objectId
                        }
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    private val _state = MutableStateFlow(CartState())
                        val state: StateFlow<CartState> = _state.onStart {
                           //initial load
                        }.stateIn(viewModelScope, SharingStarted.WhileSubscribed(5000), CartState())
                        
                        data class CartState(
                            val products: List<CartItem> = emptyList(),
                            val totalPrice: String = "",
                            val loading: Boolean = true,
                            val syncing: Boolean = false
                        )
                    """.trimIndent()
            )

            SpanText(
                text = "How stateIn Works",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.5.em).toAttrs()) {
                    SpanText(
                        text = "The stateIn operator creates a StateFlow that continuously collects the upstream Flow within the given scope. As new values are emitted by the upstream Flow, the stateFlow updates its current state and notifies all collectors.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "Explain stateIn and its SharingStarted parameter.",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.5.em).toAttrs()) {
                    SpanText(
                        text = "Converts a cold Flow into a hot StateFlow; SharingStarted controls when the upstream producer starts/stops relative to subscriber presence (WhileSubscribed, Eagerly, Lazily).",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "shareIn",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.5.em).toAttrs()) {
                    SpanText(
                        text = "shareIn is another operator that converts a cold flow into a hot SharedFlow, allowing multiple collectors to receive emissions efficiently. Unlike stateIn, shareIn doesn’t store the last emitted value.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.5.em).toAttrs()) {
                    SpanText(
                        text = "Returns a SharedFlow, which runs even if the application is in the background. This collects all emitted values by the Flowand the collected value can’t be read with a property.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            KotlinCode(
                code = """
                    val flow = flowOf(1, 2, 3, 4, 5)
                        .shareIn(
                            scope = viewModelScope,
                            started = SharingStarted.WhileSubscribed(),
                            replay = 0
                        )
                    """.trimIndent()
            )

            SpanText(
                text = "Analyzing the parameters",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li {
                    SpanText(
                        text = "The stateIn and shareIn functions have two common parameters, the scope and started.",
                        modifier = Modifier.padding(bottom = 0.3.em)
                            .fontSize(FontSize.Medium)
                    )
                }

                Ul {
                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "scope: ",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Ul {
                        Li {
                            SpanText(
                                text = "This refers to the CoroutineScope.",
                                modifier = Modifier.padding(bottom = 0.3.em)
                                    .fontSize(FontSize.Medium)
                            )
                        }
                    }
                }

                Ul {
                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "started: ",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Ul {
                        Li {
                            SpanText(
                                text = "This refers to the SharingStarted that indicates the way in which collects the emitted values by the upstream flow.",
                                modifier = Modifier.padding(bottom = 0.3.em)
                                    .fontSize(FontSize.Medium)
                            )
                        }
                    }

                    Ul {
                        Li {
                            SpanText(
                                text = "There are three types of SharingStarted : Eagerly, Lazily, and WhileSubscribed.",
                                modifier = Modifier.padding(bottom = 0.3.em)
                                    .fontSize(FontSize.Medium)
                            )
                        }
                    }
                }
            }

            SpanText(
                text = "SharingStarted types",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Ul {
                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Eagerly",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Ul {
                        Li {
                            SpanText(
                                text = "Sharing is started even before the first subscriber appears.",
                                modifier = Modifier.padding(bottom = 0.3.em)
                                    .fontSize(FontSize.Medium)
                            )
                        }

                        Li {
                            SpanText(
                                text = "Also, if the upstream flow emits multiple values that are beyond the value of the replay parameter, they won’t be accepted. (Coroutine will start regardless of whether there are active collectors or not.)",
                                modifier = Modifier.padding(bottom = 0.3.em)
                                    .fontSize(FontSize.Medium)
                            )
                        }
                    }
                }

                Ul {
                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Lazily",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Ul {
                        Li {
                            SpanText(
                                text = "Sharing is started immediately after the first subscriber appears. (Coroutine will start only when there is at least one active collector.)",
                                modifier = Modifier.padding(bottom = 0.3.em)
                                    .fontSize(FontSize.Medium)
                            )
                        }
                    }
                }

                Ul {
                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "WhileSubscribed",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Ul {
                        Li {
                            SpanText(
                                text = "Use stateIn(viewModelScope, SharingStarted.WhileSubscribed(5000), initialValue) to convert a cold flow to StateFlow in the ViewModel.",
                                modifier = Modifier.padding(bottom = 0.3.em)
                                    .fontSize(FontSize.Medium)
                            )
                        }

                        Li {
                            SpanText(
                                text = "Prefer WhileSubscribed(5000) over Eagerly — it cancels upstream collection 5 seconds after the last subscriber leaves, surviving configuration changes without leaking.",
                                modifier = Modifier.padding(bottom = 0.3.em)
                                    .fontSize(FontSize.Medium)
                            )
                        }

                        Li {
                            SpanText(
                                text = "Sharing is started when the first subscriber appears, and it stops when the last subscriber disappears, keeping the replay cache forever. (coroutine will be active as long as it has at least one active collector. In the case of multiple collectors, once the last one is canceled or completed, the coroutine will be paused or canceled.)",
                                modifier = Modifier.padding(bottom = 0.3.em)
                                    .fontSize(FontSize.Medium)
                            )
                        }

                        Li {
                            SpanText(
                                text = "When a SharedFlow or StateFlow using the SharingStarted.WhileSubscribed() strategy is collected again after the user navigates back to an Activity or Fragment, its source upstream Flow will always restart from scratch, sometimes resulting in unnecessary work being performed when the previously cached data was still valid.",
                                modifier = Modifier.padding(bottom = 0.3.em)
                                    .fontSize(FontSize.Medium)
                            )
                        }

                        Li {
                            SpanText(
                                text = "You can use WhileSubscribed(5000) most of the time to keep the upstream flow active for 5 seconds more after the disappearance of the last collector. That avoids restarting the upstream flow in certain situations such as configuration changes. This tip is especially helpful when upstream flows are expensive to create and when these operators are used in ViewModels.",
                                modifier = Modifier.padding(bottom = 0.3.em)
                                    .fontSize(FontSize.Medium)
                            )
                        }
                    }
                }

                KotlinCode(
                    code = """
                    class LocationRepository(
                        private val locationDataSource: LocationDataSource,
                        private val externalScope: CoroutineScope
                    ) {
                        val locations: Flow<Location> = 
                            locationDataSource.locationsSource
                                .shareIn(externalScope, SharingStarted.Eagerly, replay = 10)
                    } 
                    """.trimIndent()
                )

                //https://github.com/HusseinKamal/BookStore.git
                KotlinCode(
                    code = """
                        data class BookListState(
                            val searchQuery: String = "Kotlin",
                            val searchResults: List<Book> = emptyList(),
                            val favoriteBooks: List<Book> = emptyList(),
                            val isLoading: Boolean = true,
                            val selectedTabIndex: Int = 0,
                            val errorMessage: UiText? = null
                        )
                        
                        @OptIn(FlowPreview::class)
                        class BookListViewModel(
                            private val bookRepository: BookRepository
                        ) : ViewModel() {

                            private var cachedBooks = emptyList<Book>()
                            private var searchJob: Job? = null
                            private var observeFavoriteJob: Job? = null

                            private val _state = MutableStateFlow(BookListState())
                            val state = _state
                                .onStart {
                                    if(cachedBooks.isEmpty()) {
                                        observeSearchQuery()
                                    }
                                    observeFavoriteBooks()
                                }
                                .stateIn(
                                    viewModelScope,
                                    SharingStarted.WhileSubscribed(5000L),
                                    _state.value
                                )

                            fun onAction(action: BookListAction) {
                                when (action) {
                                    is BookListAction.OnBookClick -> {

                                    }

                                    is BookListAction.OnSearchQueryChange -> {
                                        _state.update {
                                            it.copy(searchQuery = action.query)
                                        }
                                    }

                                    is BookListAction.OnTabSelected -> {
                                        _state.update {
                                            it.copy(selectedTabIndex = action.index)
                                        }
                                    }
                                }
                            }

                            private fun observeFavoriteBooks() {
                                observeFavoriteJob?.cancel()
                                observeFavoriteJob = bookRepository
                                    .getFavoriteBooks()
                                    .onEach { favoriteBooks ->
                                        _state.update { it.copy(
                                            favoriteBooks = favoriteBooks
                                        ) }
                                    }
                                    .launchIn(viewModelScope)
                            }

                            private fun observeSearchQuery() {
                                state
                                    .map { it.searchQuery }
                                    .distinctUntilChanged()
                                    .debounce(500L)
                                    .onEach { query ->
                                        when {
                                            query.isBlank() -> {
                                                _state.update {
                                                    it.copy(
                                                        errorMessage = null,
                                                        searchResults = cachedBooks
                                                    )
                                                }
                                            }

                                            query.length >= 2 -> {
                                                searchJob?.cancel()
                                                searchJob = searchBooks(query)
                                            }
                                        }
                                    }
                                    .launchIn(viewModelScope)
                            }

                            private fun searchBooks(query: String) = viewModelScope.launch {
                                _state.update {
                                    it.copy(
                                        isLoading = true
                                    )
                                }
                                bookRepository
                                    .searchBooks(query)
                                    .onSuccess { searchResults ->
                                        _state.update {
                                            it.copy(
                                                isLoading = false,
                                                errorMessage = null,
                                                searchResults = searchResults
                                            )
                                        }
                                    }
                                    .onError { error ->
                                        _state.update {
                                            it.copy(
                                                searchResults = emptyList(),
                                                isLoading = false,
                                                errorMessage = error.toUiText()
                                            )
                                        }
                                    }
                            }
                        }
                    """.trimIndent()
                )

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "The stateIn function also has the initialValue parameter that, as the name suggests it defines the initial value of the Flow.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "The shareIn function also has the replay parameter. When this is set to 0 the new collectors will receive emissions that occur after they start collecting. For example, if this is set to 3 the new collectors will receive the previous 3 emitted values and also the new ones.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "When to use sharedIn",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li {
                    SpanText(
                        text = "Use shareIn when you want to share a Flow that doesn't have or need a current value.",
                        modifier = Modifier.padding(bottom = 0.3.em)
                            .fontSize(FontSize.Medium)
                    )
                }

                Li {
                    SpanText(
                        text = "we have a messaging application in which multiple users are talking in a group. We also want to enable the application to continue fetching the messages even if it is in the background.",
                        modifier = Modifier.padding(bottom = 0.3.em)
                            .fontSize(FontSize.Medium)
                    )
                }

                Li {
                    SpanText(
                        text = "This is useful when you want to share a flow that only emits values when it is subscribed to. For example, if you have a flow that listens to user input, you can use shareIn to share that flow between different parts of your app that need to react to that input.",
                        modifier = Modifier.padding(bottom = 0.3.em)
                            .fontSize(FontSize.Medium)
                    )
                }

                Li {
                    SpanText(
                        text = "When we need to display the same data in different parts of the UI.",
                        modifier = Modifier.padding(bottom = 0.3.em)
                            .fontSize(FontSize.Medium)
                    )
                }

                Li {
                    SpanText(
                        text = "When we want to perform multiple operations on the same data stream.",
                        modifier = Modifier.padding(bottom = 0.3.em)
                            .fontSize(FontSize.Medium)
                    )
                }

                Li {
                    SpanText(
                        text = "When we want to avoid duplicating the same Flow instance in multiple places.",
                        modifier = Modifier.padding(bottom = 0.3.em)
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            KotlinCode(
                code = """
                    class ChatViewModel(
                        private val chatRepository: ChatRepository
                    ) : ViewModel() {
                      val messages = chatRepository.getGroupMessages()
                      .shareIn(
                            scope = viewModelScope,
                            started = SharingStarted.WhileSubscribed(5_000),
                            replay = 1
                        )
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "When to use stateIn",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li {
                    SpanText(
                        text = "Use stateIn when you need to share a Flow that has a current value and that value can be updated by the upstream source.",
                        modifier = Modifier.padding(bottom = 0.3.em)
                            .fontSize(FontSize.Medium)
                    )
                }

                Li {
                    SpanText(
                        text = "We have a weather application that updates in real time. This will get updated only when the user has the application open.",
                        modifier = Modifier.padding(bottom = 0.3.em)
                            .fontSize(FontSize.Medium)
                    )
                }

                Li {
                    SpanText(
                        text = "This is useful when you want to share a flow that represents the state of your app, such as the user’s authentication status or their shopping cart contents. You can use stateIn to create a shared flow that emits the current state of your app, and that can be updated by different parts of your app.",
                        modifier = Modifier.padding(bottom = 0.3.em)
                            .fontSize(FontSize.Medium)
                    )
                }

                Li {
                    SpanText(
                        text = "When we need to create a Flow with a current value that can be updated by the upstream source.",
                        modifier = Modifier.padding(bottom = 0.3.em)
                            .fontSize(FontSize.Medium)
                    )
                }

                Li {
                    SpanText(
                        text = "When we want to share a Flow with a current value between multiple collectors.",
                        modifier = Modifier.padding(bottom = 0.3.em)
                            .fontSize(FontSize.Medium)
                    )
                }

                Li {
                    SpanText(
                        text = "When we want to provide an initial value for a shared Flow.",
                        modifier = Modifier.padding(bottom = 0.3.em)
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            KotlinCode(
                code = """
                    //The getWeather function returns a Flow that we’ll convert into a StateFlow to be easily collected by the UI.
                    
                    class WeatherViewModel(
                        private val weatherRepository: WeatherRepository
                    ) : ViewModel() {
                        val weather = weatherRepository.getWeather()
                            .stateIn(
                                scope = viewModelScope,
                                started = SharingStarted.WhileSubscribed(5_000),
                                initialValue = Weather()
                            )
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "Converting a flow from Cold to Hot",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li {
                    SpanText(
                        text = "A cold flow can be made hot by calling the shareIn() function on the flow.",
                        modifier = Modifier.padding(bottom = 0.3.em)
                            .fontSize(FontSize.Medium)
                    )
                }

                Li {
                    SpanText(
                        text = "This call requires a coroutine scope in which to execute the flow, a replay value, and a start policy setting indicating the conditions under which the flow is to start and stop.",
                        modifier = Modifier.padding(bottom = 0.3.em)
                            .fontSize(FontSize.Medium)
                    )
                }

                Li {
                    SpanText(
                        text = "The available start policy options are as follows:",
                        modifier = Modifier.padding(bottom = 0.3.em)
                            .fontSize(FontSize.Medium)
                    )
                }

                Ul {
                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "SharingStarted.WhileSubscribed()",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Ul {
                        Li {
                            SpanText(
                                text = "The flow is kept alive as long as it has active subscribers.",
                                modifier = Modifier.padding(bottom = 0.3.em)
                                    .fontSize(FontSize.Medium)
                            )
                        }
                    }
                }

                Ul {
                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "SharingStarted.Eagerly()",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Ul {
                        Li {
                            SpanText(
                                text = "The flow begins immediately and remains active even in the absence of active subscribers.",
                                modifier = Modifier.padding(bottom = 0.3.em)
                                    .fontSize(FontSize.Medium)
                            )
                        }
                    }
                }

                Ul {
                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "SharingStarted.Lazily()",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Ul {
                        Li {
                            SpanText(
                                text = "The flow begins only after the first consumer subscribes and remains active even in the absence of active subscribers.",
                                modifier = Modifier.padding(bottom = 0.3.em)
                                    .fontSize(FontSize.Medium)
                            )
                        }
                    }

                    KotlinCode(
                        code = """
                            fun getColdDevicesFlow(): Flow<String> = flow {
                                val devices = listOf("Android", "iOS", "Web", "Smart TV")
                                    for (device in devices) {
                                        emit(device)
                                        delay(2000) // Emit every 2 second
                                    }
                                }.shareIn(viewModelScope, SharingStarted
                                 .WhileSubscribed(replayExpirationMillis = 0))
                        """.trimIndent()
                    )
                }
            }
        }
    }
}

//https://medium.com/@rom.andrushchenko/multi-inheritance-using-marker-interfaces-fun-coding-probably-not-684a7cce5ce0